import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const WEEK_DAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseIso(s: string): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

/** Grade do calendário: semanas (cada linha 7 células). */
function buildMonthGrid(view: Date): { date: Date; inMonth: boolean }[][] {
  const first = startOfMonth(view);
  const startPad = first.getDay();
  const year = first.getFullYear();
  const month = first.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: { date: Date; inMonth: boolean }[] = [];
  const prevMonthLast = new Date(year, month, 0).getDate();
  for (let i = startPad - 1; i >= 0; i--) {
    cells.push({ date: new Date(year, month - 1, prevMonthLast - i), inMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(year, month, day), inMonth: true });
  }
  let nextDay = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    cells.push({ date: new Date(year, month + 1, nextDay), inMonth: false });
    nextDay++;
  }
  const rows: { date: Date; inMonth: boolean }[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
}

export type FinDatePickerProps = {
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Borda de erro (validação). */
  hasError?: boolean;
  /** Ao sair do botão-disparador (para marcar campo como tocado). */
  onTriggerBlur?: () => void;
};

const PANEL_MIN_WIDTH_PX = 280;
const PANEL_MARGIN_PX = 6;

/**
 * Datepicker sem input type="date". Valor em YYYY-MM-DD; exibição pt-BR.
 * O painel é renderizado via portal com `position: fixed` para não contribuir
 * para a área rolável de containers ancestrais (evita "duplo scroll").
 */
export function FinDatePicker({
  value,
  onChange,
  id,
  disabled = false,
  placeholder = 'Selecione a data',
  hasError = false,
  onTriggerBlur,
}: FinDatePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const [viewMonth, setViewMonth] = useState(() => {
    const p = parseIso(value);
    return p ?? new Date();
  });

  useLayoutEffect(() => {
    const p = parseIso(value);
    if (p) setViewMonth(p);
  }, [value, open]);

  const displayLabel = useMemo(() => {
    if (!value) return '';
    const p = parseIso(value);
    if (!p) return '';
    return p.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  }, [value]);

  const grid = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  const positionPanel = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const rect = trig.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.max(rect.width, PANEL_MIN_WIDTH_PX);
    // Sempre abre abaixo do trigger; nunca sobrepõe o input.
    // Se o painel não cabe até o fim da viewport, ele recebe max-height + scroll interno.
    const top = rect.bottom + PANEL_MARGIN_PX;
    const VIEWPORT_BOTTOM_MARGIN = 12;
    const MIN_PANEL_HEIGHT = 200;
    const maxHeight = Math.max(MIN_PANEL_HEIGHT, vh - top - VIEWPORT_BOTTOM_MARGIN);
    let left = rect.left;
    if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
    setPanelStyle({
      position: 'fixed',
      top,
      left,
      minWidth: width,
      maxHeight,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 11000,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    positionPanel();
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => positionPanel();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, positionPanel]);

  const selectDay = (cell: { date: Date; inMonth: boolean }) => {
    onChange(toIsoDate(cell.date));
    setOpen(false);
  };

  const goToday = () => {
    const t = new Date();
    onChange(toIsoDate(t));
    setOpen(false);
  };

  const panel = open ? (
    <div
      ref={panelRef}
      className="fin-datepicker__panel"
      role="dialog"
      aria-label="Calendário"
      style={panelStyle}
    >
      <div className="fin-datepicker__header">
        <button
          type="button"
          className="fin-datepicker__nav"
          onClick={() => setViewMonth((d) => addMonths(d, -1))}
          aria-label="Mês anterior"
        >
          ‹
        </button>
        <div className="fin-datepicker__title">
          {viewMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          className="fin-datepicker__nav"
          onClick={() => setViewMonth((d) => addMonths(d, 1))}
          aria-label="Próximo mês"
        >
          ›
        </button>
      </div>

      <div className="fin-datepicker__weekdays">
        {WEEK_DAYS_SHORT.map((d) => (
          <span key={d} className="fin-datepicker__wd">
            {d}
          </span>
        ))}
      </div>

      <div className="fin-datepicker__grid">
        {grid.map((row, ri) => (
          <div key={ri} className="fin-datepicker__row">
            {row.map((cell, ci) => {
              const iso = toIsoDate(cell.date);
              const isSel = value === iso;
              return (
                <button
                  key={`${ri}-${ci}`}
                  type="button"
                  className={`fin-datepicker__cell${!cell.inMonth ? ' fin-datepicker__cell--muted' : ''}${isSel ? ' fin-datepicker__cell--selected' : ''}`}
                  onClick={() => selectDay(cell)}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="fin-datepicker__footer">
        <button type="button" className="fin-datepicker__today" onClick={goToday}>
          Hoje
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`fin-datepicker${disabled ? ' fin-datepicker--disabled' : ''}${hasError ? ' fin-datepicker--error' : ''}${open ? ' fin-datepicker--open' : ''}`}
      id={id}
      aria-invalid={hasError}
    >
      <button
        ref={triggerRef}
        type="button"
        className="fin-datepicker__trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen((o) => !o)}
        onBlur={() => onTriggerBlur?.()}
      >
        <span className={displayLabel ? 'fin-datepicker__value' : 'fin-datepicker__placeholder'}>
          {displayLabel || placeholder}
        </span>
        <span className="fin-datepicker__chev" aria-hidden>▾</span>
      </button>

      {panel ? ReactDOM.createPortal(panel, document.body) : null}
    </div>
  );
}
